import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../supabaseClient';
import '../styles/Login.css';

function Register() {
    const navigate = useNavigate();
    const { signUp } = useAuth();
    const [searchParams] = useSearchParams();
    const ideFromUrl = searchParams.get('ide') || '';
    const [clientId, setClientId] = useState(ideFromUrl);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        birthDate: ''
    });
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess(false);
        setLoading(true);

        try {
            // Step 1: Verify the client IDE exists in the practitioners table
            const trimmedId = clientId.trim();
            if (!trimmedId) {
                throw new Error('L\'identifiant client (IDE) est obligatoire pour créer un compte.');
            }

            const { data: practitioners, error: practError } = await supabase
                .from('practitioners')
                .select('id, name, email');

            if (practError) throw practError;

            const matchingPractitioners = (practitioners || []).filter(p => 
                p.id.toLowerCase().startsWith(trimmedId.toLowerCase())
            );

            const practitioner = matchingPractitioners.length === 1 ? matchingPractitioners[0] : null;

            if (!practitioner) {
                throw new Error('Identifiant client (IDE) invalide. Contactez votre laboratoire pour obtenir votre identifiant.');
            }

            // Step 2: Create the auth account
            const { data, error: signUpError } = await signUp(email, password, {
                first_name: formData.firstName,
                last_name: formData.lastName,
                birth_date: formData.birthDate,
                full_name: `${formData.firstName} ${formData.lastName}`,
                practitioner_id: practitioner.id
            });

            if (signUpError) throw signUpError;

            // Step 3: Link the profile to the practitioner (using full UUID)
            if (data?.user) {
                await supabase
                    .from('profiles')
                    .update({ practitioner_id: practitioner.id })
                    .eq('id', data.user.id);
            }

            setSuccess(true);
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (err) {
            setError(err.message || 'Erreur lors de l\'inscription');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-container">
            <div className="login-card">
                <div className="login-header">
                    <h1>ENOVADENT</h1>
                    <p>Créer un compte</p>
                </div>

                <form onSubmit={handleSubmit} className="login-form">
                    <div className="form-group">
                        <label>Identifiant Client (IDE) <span className="required-star">*</span></label>
                        <input
                            type="text"
                            value={clientId}
                            onChange={(e) => setClientId(e.target.value)}
                            required
                            placeholder="Votre identifiant fourni par le laboratoire"
                            autoComplete="off"
                            className="ide-input"
                        />
                        <small className="ide-hint">
                            Cet identifiant vous a été communiqué par votre laboratoire Enovadent
                        </small>
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <label>Prénom</label>
                                <input
                                    type="text"
                                    value={formData.firstName}
                                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                                    required
                                    placeholder="Prénom"
                                    className="form-control"
                                />
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <label>Nom</label>
                                <input
                                    type="text"
                                    value={formData.lastName}
                                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                                    required
                                    placeholder="Nom"
                                    className="form-control"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Date de naissance</label>
                        <input
                            type="date"
                            value={formData.birthDate}
                            onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                            required
                            className="form-control"
                        />
                    </div>

                    <div className="form-group">
                        <label>Email</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            placeholder="votre@email.com"
                            autoComplete="email"
                            className="form-control"
                        />
                    </div>

                    <div className="form-group">
                        <label>Mot de passe</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            placeholder="••••••••"
                            minLength={6}
                            autoComplete="new-password"
                            className="form-control"
                        />
                        <small style={{ color: '#666', fontSize: '12px' }}>
                            Minimum 6 caractères
                        </small>
                    </div>

                    {error && (
                        <div className="alert alert-danger mt-3 py-2">
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className="alert alert-success mt-3 py-2">
                            Compte créé ! Vérifiez votre email pour confirmer votre inscription.
                            Redirection vers la page de connexion...
                        </div>
                    )}

                    <button type="submit" className="submit-btn mt-3" disabled={loading || success}>
                        {loading ? 'Création...' : 'Créer un compte'}
                    </button>
                </form>

                <div className="login-footer">
                    <p>
                        Déjà un compte ?
                        <Link to="/login">Se connecter</Link>
                    </p>
                </div>
            </div>
        </div>
    );
}

export default Register;
